import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { useLocalizedGalleryPhotos, useLocalizedGalleryVideos } from '../hooks/useLocalizedCMS';

const hideOnError = (e) => {
  const card = e.target.closest('[data-gallery-card]');
  if (card) card.style.display = 'none';
};

export default function GalleryPage() {
  const { t } = useLanguage();
  const g = t.gallery;
  const [activeTab, setActiveTab] = useState('photos');
  const [activeTag, setActiveTag] = useState('All');
  const { photos } = useLocalizedGalleryPhotos();
  const { videos } = useLocalizedGalleryVideos();

  const tags = useMemo(() => {
    const set = new Set(photos.map((p) => p.tag).filter(Boolean));
    return ['All', ...Array.from(set)];
  }, [photos]);

  const filtered = activeTag === 'All' ? photos : photos.filter((p) => p.tag === activeTag);

  return (
    <main>
      <SEO
        title="Gallery"
        path="/gallery"
        description="Browse product photos of ARV Rotavator, Disc Harrow, Haloom Mulcher and Cultivator in the field, at exhibitions and in our factory across Punjab."
        keywords="ARV machinery photos, rotavator gallery, disc harrow images, mulcher pictures, agricultural machinery Punjab"
      />
      {/* Hero */}
      <section className="py-16 text-center" style={{ backgroundImage: 'linear-gradient(135deg, #0A3F22 0%, #1B6B3D 100%)' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mx-auto max-w-2xl px-4">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: '#FCD34D' }}>{g.badge}</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mt-2">{g.title}</h1>
          <p className="mt-4 text-white/70 text-base">{g.subtitle}</p>
        </motion.div>
      </section>

      {/* Tabs */}
      <section className="py-6 border-b" style={{ backgroundColor: '#F9F8F4', borderBottomColor: '#E5E7EB' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center gap-4">
          {['photos', 'videos'].map((tab) => (
            <button
              key={tab}
              data-testid={`gallery-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className="px-6 py-2.5 rounded-lg font-heading font-semibold text-sm transition-all duration-200"
              style={activeTab === tab ? { backgroundColor: '#0A3F22', color: 'white' } : { color: '#4A554A' }}
            >
              {tab === 'photos' ? g.photos : g.videos}
            </button>
          ))}
        </div>
      </section>

      {activeTab === 'photos' && (
        <section className="py-10 lg:py-16" style={{ backgroundColor: '#F9F8F4' }}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Tag Filter */}
            <div className="flex flex-wrap gap-2 mb-8">
              {tags.map((tag) => (
                <button
                  key={tag}
                  data-testid={`gallery-filter-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setActiveTag(tag)}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200"
                  style={activeTag === tag ? { backgroundColor: '#0A3F22', color: 'white' } : { backgroundColor: '#E5E7EB', color: '#4A554A' }}
                >
                  {tag}
                </button>
              ))}
            </div>

            {/* Photo Grid */}
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
              {filtered.map((photo, i) => (
                <motion.div
                  key={photo.id || photo.url}
                  data-testid={`gallery-photo-${i}`}
                  data-gallery-card
                  initial={{ opacity: 0, scale: 0.97 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="break-inside-avoid rounded-xl overflow-hidden shadow-sm relative group"
                >
                  <img
                    src={photo.url}
                    alt={photo.caption}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ display: 'block' }}
                    onError={hideOnError}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4 w-full">
                      {photo.tag && <span className="text-xs font-semibold px-2 py-0.5 rounded-full mr-2" style={{ backgroundColor: '#D97706', color: 'white' }}>{photo.tag}</span>}
                      <span className="text-sm text-white font-medium">{photo.caption}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'videos' && (
        <section className="py-10 lg:py-16" style={{ backgroundColor: '#F9F8F4' }}>
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm mb-8" style={{ color: '#4A554A' }}>
              Real footage from our factory and fields. Contact us for a live demo near you.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {videos.map((video) => (
                <motion.div
                  key={video.id}
                  data-testid={`gallery-video-${video.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden shadow-sm border"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <video
                      src={video.video_url}
                      poster={video.poster_url}
                      controls
                      preload="none"
                      className="w-full h-full object-cover"
                      style={{ display: 'block' }}
                    />
                  </div>
                  <div className="p-4">
                    {video.tag && <span className="text-xs font-semibold px-2 py-0.5 rounded-full mr-2" style={{ backgroundColor: '#D97706', color: 'white' }}>{video.tag}</span>}
                    <h3 className="font-heading font-semibold text-sm leading-tight mt-2" style={{ color: '#1A1C1A' }}>{video.title}</h3>
                    <p className="text-xs mt-1" style={{ color: '#4A554A' }}>Shree Ram Agro Industries — ARV Brand</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-10 p-8 rounded-2xl" style={{ backgroundColor: '#EAF2EC' }}>
              <h3 className="font-heading font-bold text-xl mb-2" style={{ color: '#0A3F22' }}>Want a Live Demo?</h3>
              <p className="text-sm mb-4" style={{ color: '#4A554A' }}>We offer on-site demonstrations across Punjab. Contact us to schedule yours.</p>
              <a
                href="https://wa.me/917696899983"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-demo-cta"
                className="inline-block px-6 py-3 rounded-lg font-heading font-bold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#25D366' }}
              >
                WhatsApp for Demo Request
              </a>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
